import React, { useState, useEffect } from "react";
import ConfirmModal from "./ConfirmModal";
import { getAuth } from "firebase/auth";
import { getUserData } from "../services/userService";
import { useNotifications } from "../hooks/notificationsContext";

// Destructure 'style' from props so the parent grid can control its dimensions
export default function ApplyButton({ company, style }) {
  const [showModal, setShowModal] = useState(false);
  const [userData, setUserData] = useState(null);
  const [hoverOpen, setHoverOpen] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState("");

  const notifications = useNotifications();
  const pushNotification = notifications?.pushNotification;

  useEffect(() => {
    const fetchUser = async () => {
      const auth = getAuth();
      if (!auth.currentUser) return;
      const user = await getUserData(auth.currentUser.uid);
      setUserData(user);
    };
    fetchUser();
  }, []);

  useEffect(() => {
    if (!userData?.userAppliedAt) return;

    const interval = setInterval(() => {
      const now = Date.now();
      const appliedAt = userData.userAppliedAt;
      const diff = 24 * 60 * 60 * 1000 - (now - appliedAt);

      if (diff <= 0) {
        setTimeRemaining("");
      } else {
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diff % (1000 * 60)) / 1000);
        setTimeRemaining(`${hours}h ${minutes}m ${seconds}s`);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [userData]);

  const isDisabled = (() => {
    if (!userData) return true;
    const hasCompany = userData.userCompanyTicker && userData.userCompanyTicker !== "NONE";
    const now = Date.now();
    const appliedAt = userData.userAppliedAt || 0;
    const appliedRecently = now - appliedAt < 24 * 60 * 60 * 1000;
    return company.companyCEO === "NTC" || hasCompany || appliedRecently;
  })();

  const handleApply = async () => {
    setShowModal(false);
    try {
      const auth = getAuth();
      const token = await auth.currentUser.getIdToken();
      const res = await fetch("https://companyapply-5dhfwyjvzq-uc.a.run.app", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ companyTicker: company.companyTicker }),
      });

      const data = await res.json();
      if (res.ok && (data.success || data.status === "success")) {
        pushNotification?.({ type: "success", message: "Application submitted." });
        const updatedUser = await getUserData(auth.currentUser.uid);
        setUserData(updatedUser);
      } else {
        pushNotification?.({ type: "error", message: data.message || "Failed." });
      }
    } catch (err) {
      pushNotification?.({ type: "error", message: "Error occurred." });
    }
  };

  return (
    <>
      <div
        style={{ 
          display: "flex", // Changed to flex to fill grid cell
          position: "relative", 
          width: "100%", 
          height: "100%" 
        }}
        onMouseEnter={() => setHoverOpen(true)}
        onMouseLeave={() => setHoverOpen(false)}
      >
        <button
          className={`jetbrains-btn ${isDisabled ? "disabled" : ""}`}
          onClick={() => !isDisabled && setShowModal(true)}
          disabled={isDisabled}
          style={{
            ...style, // Spread the incoming grid styles (padding, font size, etc.)
            cursor: isDisabled ? "not-allowed" : "pointer",
            opacity: isDisabled ? 0.3 : 1,
            width: "100%",
            height: "100%",
            textTransform: "uppercase",
            fontFamily: "JetBrains Mono, monospace"
          }}
        >
          APPLY
        </button>

        {hoverOpen && (
          <div
            style={{
              position: "absolute",
              left: "50%",
              bottom: "calc(100% + 10px)",
              transform: "translateX(-50%)",
              background: "#111",
              color: "#50E3C2",
              border: "1px solid rgba(80,227,194,0.4)",
              borderRadius: 4,
              padding: "8px 12px",
              minWidth: "160px",
              fontSize: "0.65rem",
              fontFamily: "JetBrains Mono",
              textAlign: "center",
              boxShadow: "0 4px 12px rgba(0,0,0,0.8)",
              zIndex: 9999,
              pointerEvents: "none"
            }}
          >
            {isDisabled
              ? timeRemaining
                ? `COOLDOWN: ${timeRemaining}`
                : userData?.userCompanyTicker !== "NONE"
                ? "MUST BE UNEMPLOYED"
                : "UNAVAILABLE"
              : `APPLY TO ${company.companyTicker}`}
            
            {/* Arrow */}
            <div style={{
                position: "absolute",
                top: "100%",
                left: "50%",
                width: 6,
                height: 6,
                background: "#111",
                borderLeft: "1px solid rgba(80,227,194,0.4)",
                borderBottom: "1px solid rgba(80,227,194,0.4)",
                transform: "translateX(-50%) rotate(45deg)",
                marginTop: "-3px",
            }} />
          </div>
        )}
      </div>

      {showModal && (
        <ConfirmModal
          title="CONTRACT_SUBMISSION"
          message={`Submit work application to ${company.companyName}?`}
          confirmText="SUBMIT"
          cancelText="CANCEL"
          onConfirm={handleApply}
          onCancel={() => setShowModal(false)}
        />
      )}
    </>
  );
}